import moment from 'moment';

/** Timestamp do sortowania / kafelka: `created_at` (ISO), ewent. `createdAt`; inaczej dzień `date` + lekki offset `id`. */
export function bloodPressureReadingTimeMs(r) {
  if (!r) return 0;
  const created = r.created_at ?? r.createdAt;
  if (created) {
    const t = new Date(created).getTime();
    if (Number.isFinite(t)) return t;
  }
  if (r.date) {
    const t = new Date(`${r.date}T12:00:00Z`).getTime();
    if (!Number.isFinite(t)) return 0;
    const idN = Number(r.id);
    if (Number.isFinite(idN)) return t + idN * 1000;
    return t;
  }
  return 0;
}

/** Pierwszy sensowny timestamp z rekordu API (snake_case / camelCase / measured_at). */
export function bloodPressureRecordMoment(r) {
  if (!r) return null;
  const candidates = [
    r.created_at,
    r.createdAt,
    r.measured_at,
    r.measuredAt,
    r.recorded_at,
    r.recordedAt,
    r.updated_at,
    r.updatedAt,
    r.timestamp,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (c == null || c === '') continue;
    const m = moment(c);
    if (m.isValid()) return m;
  }
  return null;
}

/**
 * Dzień kalendarzowy (oś X — jedna kolumna na dzień).
 * Najpierw timestamp zapisu / pomiaru; pole `date` bywa identyczne dla wielu rekordów — wtedy fallback.
 */
export function bloodPressureCalendarDayKey(r) {
  if (!r) return '';
  const fromTs = bloodPressureRecordMoment(r);
  if (fromTs) return fromTs.format('YYYY-MM-DD');
  const rawDate = r.date != null ? String(r.date).trim() : '';
  if (rawDate) {
    const strict = moment.utc(rawDate, 'YYYY-MM-DD', true);
    if (strict.isValid()) return strict.format('YYYY-MM-DD');
    const loose = moment.utc(rawDate);
    if (loose.isValid()) return loose.format('YYYY-MM-DD');
  }
  return '';
}

/** Od najstarszego; przy tym samym czasie — po `id`. */
export function compareBloodPressureReadingsOldestFirst(a, b) {
  const ta = bloodPressureReadingTimeMs(a);
  const tb = bloodPressureReadingTimeMs(b);
  if (ta !== tb) return ta - tb;
  const ida = Number(a.id);
  const idb = Number(b.id);
  if (Number.isFinite(ida) && Number.isFinite(idb) && ida !== idb) return ida - idb;
  return 0;
}

/** Kopia posortowana: najstarszy → najnowszy. */
export function sortBloodPressureReadingsOldestToNewest(readings) {
  if (!readings?.length) return [];
  return [...readings].sort(compareBloodPressureReadingsOldestFirst);
}

/**
 * Serie [[indeksDnia, mmHg], …] + etykiety — używane na stronie kalkulatorów (wykres punktowy).
 */
export function buildBloodPressureDayChartModel(readings, lang) {
  const rows = (readings || [])
    .map((r) => ({ r, day: bloodPressureCalendarDayKey(r) }))
    .filter(({ day }) => day);
  if (!rows.length) return null;

  const dayKeys = [...new Set(rows.map((x) => x.day))].sort((a, b) => a.localeCompare(b));
  const dayLabels = dayKeys.map((d) =>
    lang === 'pl'
      ? moment(d, 'YYYY-MM-DD').format('DD.MM.YYYY')
      : moment(d, 'YYYY-MM-DD').format('MMM D, YYYY'),
  );
  const dayToIndex = Object.fromEntries(dayKeys.map((d, i) => [d, i]));

  const sortedRows = [...rows].sort((a, b) => {
    const c = a.day.localeCompare(b.day);
    if (c !== 0) return c;
    return compareBloodPressureReadingsOldestFirst(a.r, b.r);
  });

  const sysData = sortedRows.map(({ r, day }) => [dayToIndex[day], Number(r.systolic)]);
  const diaData = sortedRows.map(({ r, day }) => [dayToIndex[day], Number(r.diastolic)]);
  const yVals = sortedRows.flatMap(({ r }) => [Number(r.systolic), Number(r.diastolic)]);
  const yMin = Math.min(...yVals);
  const yMax = Math.max(...yVals);

  return {
    pointReadings: sortedRows.map(({ r }) => r),
    dayKeys,
    dayLabels,
    sysData,
    diaData,
    nDays: dayKeys.length,
    yMin,
    yMax,
  };
}

/**
 * Jedna wartość na dzień kalendarzowy: max skurczowe i max rozkurczowe (wiele pomiarów tego samego dnia).
 * Zwraca tablicę posortowaną rosnąco po dayKey.
 */
export function aggregateBloodPressureDailyMax(readings) {
  const valid = (readings || []).filter(
    (r) =>
      r &&
      bloodPressureCalendarDayKey(r) &&
      Number.isFinite(Number(r.systolic)) &&
      Number.isFinite(Number(r.diastolic)),
  );
  const byDay = new Map();
  for (const r of valid) {
    const day = bloodPressureCalendarDayKey(r);
    const sys = Number(r.systolic);
    const dia = Number(r.diastolic);
    const cur = byDay.get(day);
    if (!cur) {
      byDay.set(day, { dayKey: day, systolic: sys, diastolic: dia });
    } else {
      byDay.set(day, {
        dayKey: day,
        systolic: Math.max(cur.systolic, sys),
        diastolic: Math.max(cur.diastolic, dia),
      });
    }
  }
  return [...byDay.values()].sort((a, b) => a.dayKey.localeCompare(b.dayKey));
}

/** Ostatni dzień z danymi (najnowszy kalendarzowo). */
export function getLatestDailyBloodPressure(dailyAggregates) {
  if (!dailyAggregates?.length) return null;
  return dailyAggregates[dailyAggregates.length - 1];
}
